import { BaseViewHTML } from '../../systems/base-view-html.js';
import { designTokens, escapeHtml } from '../../core/design-system.js';
import { bus } from '../../core/events.js';

export interface CalculatorStep {
  action: string; // The button value or command (e.g. '1', '+', 'C', '=')
  description?: string; // Optional text to show for this step
}

export interface CalculatorSequenceState {
  steps: CalculatorStep[];
  currentIndex: number;
  isPlaying: boolean;
  title?: string;
}

export interface CalculatorState {
  displayValue: string;
  history: string[];
  mode: 'interactive' | 'remote-only';
  sequence?: CalculatorSequenceState; // Optional sequence state
}

export class CalculatorView extends BaseViewHTML {
  private state: CalculatorState;

  constructor(
    processId: string,
    viewId: string,
    initialState: CalculatorState
  ) {
    super(processId, viewId, {
      preferredRatio: 0.75, // Portrait orientation like a calculator
      showDismissButton: true,
      sizes: [0, 1], // Support half and full height
    });
    this.state = initialState;
  }

  public updateState(newState: CalculatorState): void {
    this.state = newState;
    this.markDirty();
  }

  protected getContentHTML(): string {
    const { displayValue, history, mode, sequence } = this.state;
    // Force interactive mode if no sequence is active or if the sequence is paused
    const isRemoteOnly = mode === 'remote-only' && sequence?.isPlaying;
    
    // Determine if we should show playback controls
    const showPlayback = sequence && sequence.steps.length > 0;

    // Calculate height adjustments if playback controls are present
    const contentGap = designTokens.padding.md;

    return `
      <div style="display: flex; flex-direction: column; height: 100%; gap: ${contentGap}; position: relative;">
        
        ${isRemoteOnly ? `
          <div style="
            position: absolute;
            top: -5px;
            right: -5px;
            background: ${designTokens.accent};
            color: #000;
            font-size: 10px;
            padding: 2px 6px;
            border-radius: 4px;
            font-weight: bold;
            z-index: 10;
            pointer-events: none;
          ">AI CONTROL</div>
        ` : ''}

        ${showPlayback ? this.renderPlaybackHeader(sequence!) : ''}

        <!-- Display Area -->
        <div style="
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid ${designTokens.borderColor};
          border-radius: ${designTokens.radius.md};
          padding: ${designTokens.padding.md};
          text-align: right;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          min-height: 80px;
        ">
          <div style="
            font-size: ${designTokens.fontSize.xs};
            color: ${designTokens.contentColorMuted};
            margin-bottom: 4px;
            height: 1.2em;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          ">
            ${history.length > 0 ? escapeHtml(history[history.length - 1]) : ''}
          </div>
          <div style="
            font-size: 32px;
            font-weight: 600;
            color: ${designTokens.contentColor};
            font-family: monospace;
            overflow: hidden;
            text-overflow: ellipsis;
          ">
            ${escapeHtml(displayValue)}
          </div>
        </div>

        <!-- Keypad -->
        <div style="
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: ${designTokens.padding.sm};
          flex: 1;
          opacity: ${isRemoteOnly ? '0.6' : '1'};
          pointer-events: ${isRemoteOnly ? 'none' : 'auto'};
          transition: opacity 0.3s ease;
          min-height: 0; /* Allow shrinking */
        ">
          ${this.renderButton('C', 'secondary')}
          ${this.renderButton('(', 'secondary')}
          ${this.renderButton(')', 'secondary')}
          ${this.renderButton('/', 'primary')}

          ${this.renderButton('7')}
          ${this.renderButton('8')}
          ${this.renderButton('9')}
          ${this.renderButton('*', 'primary')}

          ${this.renderButton('4')}
          ${this.renderButton('5')}
          ${this.renderButton('6')}
          ${this.renderButton('-', 'primary')}

          ${this.renderButton('1')}
          ${this.renderButton('2')}
          ${this.renderButton('3')}
          ${this.renderButton('+', 'primary')}

          ${this.renderButton('0', 'default', 'grid-column: span 2;')}
          ${this.renderButton('.')}
          ${this.renderButton('=', 'accent')}
        </div>
        
        ${showPlayback ? this.renderPlaybackControls(sequence!) : ''}
      </div>
    `;
  }

  private renderPlaybackHeader(sequence: CalculatorSequenceState): string {
    const currentStep = sequence.steps[sequence.currentIndex];
    const stepDescription = currentStep?.description || `Step ${sequence.currentIndex + 1} of ${sequence.steps.length}`;

    return `
      <div style="
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid ${designTokens.borderColor};
        border-radius: ${designTokens.radius.sm};
        padding: ${designTokens.padding.sm};
        font-size: ${designTokens.fontSize.sm};
        color: ${designTokens.contentColor};
        display: flex;
        flex-direction: column;
        gap: 4px;
      ">
        ${sequence.title ? `<div style="font-weight: 600; color: ${designTokens.accent}; font-size: ${designTokens.fontSize.xs}; text-transform: uppercase;">${escapeHtml(sequence.title)}</div>` : ''}
        <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
          ${escapeHtml(stepDescription)}
        </div>
      </div>
    `;
  }

  private renderPlaybackControls(sequence: CalculatorSequenceState): string {
    const isPlaying = sequence.isPlaying;
    
    return `
      <div style="
        display: flex;
        gap: ${designTokens.padding.sm};
        margin-top: auto;
      ">
        <button class="playback-btn" data-action="prev" style="${this.getPlaybackButtonStyle()}">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z"/></svg>
        </button>
        
        <button class="playback-btn" data-action="toggle-play" style="${this.getPlaybackButtonStyle(true)}">
          ${isPlaying 
            ? '<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>' 
            : '<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>'
          }
        </button>
        
        <button class="playback-btn" data-action="next" style="${this.getPlaybackButtonStyle()}">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z"/></svg>
        </button>
        
        <div style="
          flex: 1;
          display: flex;
          align-items: center;
          padding: 0 8px;
        ">
          <div style="
            width: 100%;
            height: 4px;
            background: rgba(255,255,255,0.1);
            border-radius: 2px;
            overflow: hidden;
          ">
            <div style="
              width: ${((sequence.currentIndex + 1) / sequence.steps.length) * 100}%;
              height: 100%;
              background: ${designTokens.accent};
              transition: width 0.3s ease;
            "></div>
          </div>
        </div>
      </div>
    `;
  }

  private getPlaybackButtonStyle(primary: boolean = false): string {
    return `
      flex: 0 0 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: ${primary ? 'rgba(102, 204, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
      border: 1px solid ${primary ? 'rgba(102, 204, 255, 0.4)' : designTokens.borderColor};
      border-radius: 4px;
      color: ${designTokens.contentColor};
      cursor: pointer;
      transition: all 0.2s;
    `;
  }

  private renderButton(label: string, variant: 'default' | 'primary' | 'secondary' | 'accent' = 'default', styleOverride: string = ''): string {
    let bg = 'rgba(255, 255, 255, 0.05)';
    let color = designTokens.contentColor;
    let border = designTokens.borderColor;

    if (variant === 'primary') {
      bg = 'rgba(102, 204, 255, 0.15)'; // Cyan tint
      border = 'rgba(102, 204, 255, 0.3)';
    } else if (variant === 'secondary') {
      bg = 'rgba(255, 255, 255, 0.1)';
      color = designTokens.contentColorMuted;
    } else if (variant === 'accent') {
      bg = designTokens.accent;
      color = '#000'; // Black text on cyan
    }

    // Highlight button if it matches current sequence step
    const { sequence } = this.state;
    let isHighlighted = false;
    if (sequence && sequence.currentIndex < sequence.steps.length) {
      const currentStep = sequence.steps[sequence.currentIndex];
      if (currentStep.action === label) {
        isHighlighted = true;
        bg = designTokens.accent;
        color = '#000';
        border = designTokens.accent;
      }
    }

    return `
      <button 
        class="calculator-btn" 
        data-value="${label}"
        style="
          background: ${bg};
          border: 1px solid ${border};
          border-radius: ${designTokens.radius.sm};
          color: ${color};
          font-size: ${designTokens.fontSize.lg};
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          ${isHighlighted ? 'box-shadow: 0 0 10px rgba(102, 204, 255, 0.5); transform: scale(1.05); z-index: 2;' : ''}
          ${styleOverride}
        "
        onmouseover="this.style.filter='brightness(1.2)'"
        onmouseout="this.style.filter='none'"
        onmousedown="this.style.transform='scale(0.95)'"
        onmouseup="this.style.transform='scale(1)'"
      >
        ${label}
      </button>
    `;
  }

  // Override mount to attach event listeners
  mount(container: HTMLElement): void {
    super.mount(container);
    
    // Attach click listener to the container for event delegation
    if (this.element) {
      this.element.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        
        // Check for playback controls
        const playbackBtn = target.closest('.playback-btn') as HTMLElement;
        if (playbackBtn) {
          const action = playbackBtn.dataset.action;
          if (action) {
            e.stopPropagation(); // Prevent triggering other clicks
            bus.emit('calculator.playback', {
              processId: this.processId,
              action: action
            });
            return;
          }
        }

        // Check for keypad buttons
        // Relaxed condition: Only block if actively playing
        if (this.state.mode === 'remote-only' && this.state.sequence?.isPlaying) return;

        const button = target.closest('.calculator-btn') as HTMLElement;
        if (button) {
          const value = button.getAttribute('data-value');
          if (value) {
            this.handleInput(value);
          }
        }
      });
    }
  }

  private handleInput(value: string): void {
    bus.emit('calculator.action', {
      processId: this.processId,
      action: value
    });
  }
}
